import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { IAutentiqueSignatarioEventos, useAutentique } from '../../hooks/useAutentique';
import { EMAIL_REGEX } from '../../pages/estatutoPublico/PessoasField';
import { extractApiErrorMessage } from '../../utils/ErrorUtils';

interface CorrigirAssinanteModalProps {
  cardId: number;
  assinante: IAutentiqueSignatarioEventos | null;
  onClose: () => void;
  onCorrigido: () => void;
  onFalhou: () => void;
}

export const CorrigirAssinanteModal: React.FC<CorrigirAssinanteModalProps> = ({
  cardId,
  assinante,
  onClose,
  onCorrigido,
  onFalhou,
}) => {
  const { corrigirSignatario } = useAutentique();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const corrigir = useMutation({
    mutationFn: () =>
      corrigirSignatario(cardId, assinante?.signatario_id as number, {
        nome: nome.trim(),
        email: email.trim(),
      }),
    onSuccess: onCorrigido,
    onError: onFalhou,
  });
  const { reset } = corrigir;
  // Com a troca em andamento não fecha: o erro só aparece aqui.
  const fechar = () => {
    if (!corrigir.isPending) onClose();
  };

  useEffect(() => {
    setNome(assinante?.nome ?? '');
    setEmail(assinante?.email ?? '');
    reset();
  }, [assinante, reset]);

  const emailNovo = email.trim().toLowerCase() !== assinante?.email.toLowerCase();
  const podeSalvar = !!nome.trim() && EMAIL_REGEX.test(email.trim()) && emailNovo;

  return (
    <Modal isOpen={!!assinante} onClose={fechar} size="md" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader fontSize="16px">Corrigir assinante</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={3}>
            <FormLabel fontSize="12px">Nome</FormLabel>
            <Input size="sm" value={nome} onChange={e => setNome(e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="12px">E-mail</FormLabel>
            <Input size="sm" value={email} onChange={e => setEmail(e.target.value)} />
          </FormControl>
          <Text fontSize="12px" color="gray.500" mt={3}>
            Informe o e-mail novo para salvar. O convite sai para ele e o anterior deixa de valer.
            Quem já assinou não assina de novo.
          </Text>
          {assinante?.status === 'rejeitado' && (
            <Text fontSize="12px" color="orange.600" mt={2}>
              Esta pessoa recusou. A recusa sai do envio e fica registrada na atividade do card.
            </Text>
          )}
          {corrigir.isError && (
            <Text fontSize="12px" color="red.500" mt={2}>
              {extractApiErrorMessage(
                corrigir.error,
                'Não foi possível corrigir. Confira os dados e tente de novo.',
              )}
            </Text>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" isDisabled={corrigir.isPending} onClick={fechar}>
            Voltar
          </Button>
          <Button
            size="sm"
            colorScheme="purple"
            isDisabled={!podeSalvar}
            isLoading={corrigir.isPending}
            onClick={() => corrigir.mutate()}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
